import type { SafetyVideoStatus } from "@unbox/api";
import {
  AlertTriangle,
  CheckSquare,
  CheckVerified02,
  CursorBox,
  EyeOff,
  FileX03,
  InfoCircle,
  Lock01,
  ShieldTick,
  VideoRecorderOff,
} from "@untitledui/icons";
import type { ComponentType, ReactNode, SVGProps } from "react";
import { Focusable } from "react-aria-components";
import { Tooltip } from "@/components/base/tooltip/tooltip";
import { Badge } from "@/components/ui/badge";
import { cx } from "@/lib/utils";
import { formatDate } from "@/utils/date-conversion";
import { AiReviewedIcon, HumanReviewedIcon } from "./review-source-icons";

type StatusDisplay = {
  label: string;
  icon: typeof FileX03;
  badgeClasses: string;
  /** Trails the label to say whether the outcome came from our analysis or from a person. */
  sourceIcon?: ComponentType<SVGProps<SVGSVGElement>>;
  /** Sits behind the info icon. Only the statuses that need explaining carry one. */
  tooltip?: { title: string; body: ReactNode };
};

const statusDisplay: Record<SafetyVideoStatus, StatusDisplay> = {
  noRisksFound: {
    label: "No Risks Found",
    icon: CheckVerified02,
    badgeClasses: "bg-utility-green-50 text-success-primary hover:bg-utility-green-100 border-utility-green-200",
    sourceIcon: AiReviewedIcon,
  },
  noRisksFoundAndManuallyConfirmed: {
    label: "No Risks - reviewed",
    icon: ShieldTick,
    badgeClasses: "bg-utility-green-50 text-success-primary hover:bg-utility-green-100 border-utility-green-200",
    sourceIcon: HumanReviewedIcon,
  },
  riskNeedsReviewing: {
    label: "Needs Reviewing",
    icon: AlertTriangle,
    badgeClasses: "bg-error-primary text-error-primary hover:bg-error-secondary border-error_subtle",
  },
  riskNeedsAction: {
    label: "Needs Action",
    icon: CursorBox,
    badgeClasses: "bg-warning-primary text-warning-primary hover:bg-warning-secondary border-fg-warning-secondary",
  },
  actionInProgress: {
    label: "Action In Progress",
    icon: CursorBox,
    badgeClasses: "bg-utility-blue-50 text-utility-blue-700 hover:bg-utility-blue-100 border-utility-blue-light-300",
  },
  noActionRequired: {
    label: "No Action Required",
    icon: CheckSquare,
    badgeClasses:
      "bg-utility-gray-blue-50 text-utility-gray-blue-700 hover:bg-utility-gray-blue-100 border-utility-gray-blue-200",
  },
  riskActioned: {
    label: "Risk Actioned",
    icon: CheckSquare,
    badgeClasses:
      "bg-utility-gray-blue-50 text-utility-gray-blue-700 hover:bg-utility-gray-blue-100 border-utility-gray-blue-200",
  },
  unableToAnalyse: {
    label: "Unable to Analyse",
    icon: FileX03,
    badgeClasses:
      "bg-utility-warning-50 text-utility-warning-700 hover:bg-utility-warning-100 border-utility-warning-200",
    sourceIcon: AiReviewedIcon,
    tooltip: {
      title: "Unable to Analyse video",
      body: "An error occurred while trying to analyse this video. If you still need this video analysed, please contact support",
    },
  },
  unableToAccess: {
    label: "Unable to Access",
    icon: VideoRecorderOff,
    badgeClasses:
      "bg-utility-warning-50 text-utility-warning-700 hover:bg-utility-warning-100 border-utility-warning-200",
    tooltip: {
      title: "Unable to Access video",
      body: "Our system was unable to access this video to detect if there were any risks.",
    },
  },
  videoPrivated: {
    label: "Video Private",
    icon: Lock01,
    badgeClasses: "bg-utility-blue-50 text-utility-blue-700 hover:bg-utility-blue-100 border-utility-blue-200",
    sourceIcon: AiReviewedIcon,
    tooltip: {
      title: "Video Private",
      body: (
        <>
          We don't have permission to access this video. This can happen for a few reasons:
          <ul className="mt-2 list-disc pl-4">
            <li>Set to private by the creator</li>
            <li>Age-restricted by the creator (our analysis method is unable to verify age to gain access)</li>
          </ul>
        </>
      ),
    },
  },
  videoRemoved: {
    label: "Video removed",
    icon: VideoRecorderOff,
    badgeClasses:
      "bg-utility-gray-blue-50 text-utility-gray-700 hover:bg-utility-gray-blue-100 border-utility-gray-blue-200",
    tooltip: {
      title: "Video removed",
      body: "This video has been removed and is no longer available",
    },
  },
  historic: {
    label: "Historical",
    icon: EyeOff,
    badgeClasses: "bg-utility-gray-50 text-utility-gray-700 hover:bg-utility-gray-100 border-utility-gray-200",
    tooltip: {
      title: "Historical",
      body: "This video was published before joining Unbox. Safety screening only runs on videos published after your account was set up.",
    },
  },
};

export const VIDEO_STATUS_OPTIONS = Object.entries(statusDisplay).map(([id, { label }]) => ({ id, label }));

export const getVideoStatusLabel = (status: string) => statusDisplay[status as SafetyVideoStatus]?.label ?? status;

export const DetailedVideoStatusBadge = ({
  status,
  showInfoIcon = false,
  showStatusIcon = false,
  reviewedBy,
  withTooltip = true,
}: {
  status: SafetyVideoStatus;
  showInfoIcon?: boolean;
  /** Off when the badge is the trigger of another popover, which then carries the explanation. */
  withTooltip?: boolean;
  /** The leading icon. Carried by the badge on a video's own page, dropped in tables and lists. */
  showStatusIcon?: boolean;
  /** Who confirmed a manually reviewed video, and when. Shown in the badge's tooltip. */
  reviewedBy?: { name: string; at: string | Date };
}) => {
  if (!status) return null;

  const { label, icon: Icon, badgeClasses, tooltip, sourceIcon: SourceIcon } = statusDisplay[status];

  const badge = (
    <Badge className={cx(badgeClasses, "w-fit")}>
      {showStatusIcon && !SourceIcon && <Icon className="w-3 h-3 mr-1" />}
      {label}
      {SourceIcon ? (
        <SourceIcon aria-hidden className="ml-1.5 size-4 shrink-0" />
      ) : (
        (showInfoIcon || tooltip) && <InfoCircle className="w-3 h-3 ml-1" />
      )}
    </Badge>
  );

  if (!withTooltip) return badge;

  if (status === "noRisksFoundAndManuallyConfirmed" && reviewedBy) {
    return (
      <Tooltip
        title={`This was manually reviewed and confirmed as having no risks by ${reviewedBy.name} - ${formatDate(reviewedBy.at)}`}
      >
        <Focusable>
          <span className="inline-flex">{badge}</span>
        </Focusable>
      </Tooltip>
    );
  }

  if (!tooltip) return badge;

  // Focusable rather than TooltipTrigger, which renders a button: the badge sits inside
  // clickable table rows, where a button swallows the row click. The tooltip still needs a
  // registered trigger to anchor against — without one it positions against the viewport.
  return (
    <Tooltip title={tooltip.title} description={tooltip.body}>
      <Focusable>
        <span className="inline-flex">{badge}</span>
      </Focusable>
    </Tooltip>
  );
};
